'use client';

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Button, buttonVariants } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib';
import { X } from 'lucide-react';
import type * as React from 'react';

function AlertDialog({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogPortal({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        'bg-opacity-90 bg-grey-50/70 dark:bg-grey-1100/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50',
        className
      )}
      {...props}
    />
  );
}

function AlertDialogContent({
  className,
  alertDialogOverlayClassName,
  hideCloseButton = true,
  children,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content> & {
  alertDialogOverlayClassName?: string;
  hideCloseButton?: boolean;
}) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay className={alertDialogOverlayClassName} />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        className={cn(
          'dark:bg-grey-1000 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[335px] translate-x-[-50%] translate-y-[-50%] gap-5 rounded-3xl bg-neutral-100 p-8 shadow-[0px_17px_38px_0px_rgba(42,43,65,0.05)] duration-200 sm:max-w-lg md:gap-7 md:p-11',
          className
        )}
        {...props}
      >
        {!hideCloseButton && (
          <AlertDialogPrimitive.Cancel asChild>
            <Button
              variant="primary-icon"
              aria-label="Close dialog"
              className="ml-auto [&[data-variant*=icon]]:h-8 [&[data-variant*=icon]]:w-8 [&[data-variant*=icon]_svg]:h-[24px] [&[data-variant*=icon]_svg]:w-[24px]"
            >
              <X />
            </Button>
          </AlertDialogPrimitive.Cancel>
        )}
        {children}
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn('flex flex-col gap-5 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn('flex flex-col gap-4', className)}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(
        'text-2xl font-bold text-neutral-200 md:text-3xl md:leading-10 dark:text-neutral-100',
        className
      )}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(
        'text-grey-600 dark:text-grey-400 text-sm leading-tight font-normal',
        className
      )}
      {...props}
    />
  );
}

function AlertDialogAction({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action>) {
  return <AlertDialogPrimitive.Action className={cn(buttonVariants(), className)} {...props} />;
}

function AlertDialogCancel({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
  return (
    <AlertDialogPrimitive.Cancel
      className={cn(buttonVariants({ variant: 'secondary' }), className)}
      {...props}
    />
  );
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};
